import { EditOutlined } from '@ant-design/icons';
import { Tag, TagProps, Typography } from 'antd';
import { ReactNode } from 'react';
import { useIntl } from 'react-intl';

import { ProductStateStatus } from 'protocol/api/billing_new/dto_product_new_pb';

import { ENVIRONMENT } from '@/shared/config';

import { i18n } from '@/entities/billing';

import { useSubscriptionExpiresModalForm } from '../../hooks/useSubscriptionExpiresModalForm';
import { SubscriptionExpiresModalForm } from '../SubscriptionExpiresModalForm/SubscriptionExpiresModalForm';

import styles from './SubscriptionSummary.module.scss';

const { Text } = Typography;

export const SUBSCRIPTION_STATUS_COLOR: Record<
  ProductStateStatus,
  TagProps['color']
> = {
  [ProductStateStatus.ProductStateStatusActive]: 'green',
  [ProductStateStatus.ProductStateStatusAwaitingActivation]: 'orange',
  [ProductStateStatus.ProductStateStatusLifeTimeIsOver]: 'default',
  [ProductStateStatus.ProductStateStatusDeleted]: 'red',
  [ProductStateStatus.ProductStateStatusInvalidValue]: 'default',
};

type SubscriptionSummaryProps = {
  title: string;
  startDate: number;
  endDate: number;
  productID: string;
  status: ProductStateStatus;
  isAutoRenewal?: boolean;
  currency?: string;
  price?: number;
  customPrice?: number;
  showStatusTag?: boolean;
  // Rendered instead of "Lifetime" when the subscription has no dates yet.
  // A limit-scheduled subscription has Start and End both zero until its trigger
  // fires, which would otherwise read as an unlimited plan.
  dateRangeFallback?: ReactNode;
};

export const SubscriptionSummary = (props: SubscriptionSummaryProps) => {
  const {
    title,
    startDate,
    endDate,
    productID,
    status,
    currency = 'USD',
    price = 0,
    customPrice,
    isAutoRenewal = true,
    showStatusTag = false,
    dateRangeFallback,
  } = props;

  const { formatMessage, formatDate, formatNumber, formatDateTimeRange } =
    useIntl();

  const { loading, visible, toggleVisible, setSubscriptionExpiresAfter } =
    useSubscriptionExpiresModalForm(productID);

  const formatCost = (value: number) =>
    formatNumber(value, {
      style: 'currency',
      maximumFractionDigits: 2,
      currencyDisplay: 'narrowSymbol',
      currency,
    });
  const cost = formatCost(price);

  return (
    <div className={styles.container}>
      <Text className={styles.title} strong>
        {title}
      </Text>

      {(price > 0 || customPrice !== undefined) && (
        <span>
          {customPrice === undefined
            ? formatMessage(
                {
                  id: 'SubscriptionSummary.cost',
                  defaultMessage: 'Cost: {cost}',
                },
                {
                  cost,
                },
              )
            : formatMessage(
                {
                  id: 'SubscriptionSummary.costSoldAt',
                  defaultMessage: 'Cost: {cost} (sold at {customCost})',
                },
                {
                  cost,
                  customCost: formatCost(customPrice),
                },
              )}
        </span>
      )}

      <span className={styles.date}>
        {startDate || endDate
          ? formatDateTimeRange(startDate, endDate)
          : dateRangeFallback ??
            formatMessage({
              id: 'lifetime.month.0',
              defaultMessage: 'Lifetime',
            })}

        {ENVIRONMENT !== 'production' && (
          // NOTE: for some reason this component require 'rev' prop, but i duno what it is
          <EditOutlined onClick={() => toggleVisible(true)} rev={undefined} />
        )}
      </span>

      {isAutoRenewal || showStatusTag ? (
        <Tag color={SUBSCRIPTION_STATUS_COLOR[status]}>
          {formatMessage(i18n.subscriptionStatusMessages[status])}
        </Tag>
      ) : (
        <Tag color="orange">
          {formatMessage({
            id: 'SubscriptionSummary.expiresOnStatus',
            defaultMessage: 'Expires on',
          })}{' '}
          {formatDate(endDate)}
        </Tag>
      )}

      <SubscriptionExpiresModalForm
        confirmLoading={loading}
        onSubmit={setSubscriptionExpiresAfter}
        visible={visible}
        onCancel={() => toggleVisible(false)}
      />
    </div>
  );
};
